import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useReadContract } from "wagmi";
import { addresses, presaleAbi } from "../config/contracts";
import { AnimatedNumber } from "./AnimatedNumber";
import { ProgressBar } from "./ProgressBar";
import { Countdown } from "./Countdown";
import { ZONE_ORDER, type ZoneId } from "./spatial/constants";

const HAS_PRESALE =
  addresses.presale !== "0x0000000000000000000000000000000000000000";

/**
 * DOM overlay above the spatial scene that reveals zone-specific copy
 * as the user scrolls past each stop.
 *
 * Why DOM, not 3D <Text>
 * - Real HTML text is hyper-crisp, accessible (screen readers + Cmd-F),
 *   and doesn't fight Vision Pro's font-rendering passthrough.
 * - We can use Inter / Bebas with proper letter-spacing and ligatures.
 *
 * Only the hero zone gets a CTA (Buy / Connect). All other zones get a
 * one-line lead + a quiet hint to "scroll" or "tap a beacon" — the user
 * naturally moves down the page; the camera follows.
 */
export function ScrollOverlay() {
  const [zone, setZone] = useState<ZoneId>("hero");
  const [pastSpatial, setPastSpatial] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      // Spatial section is the first 600vh; after that, the user is in the
      // deep-dive 2D widgets and we should fade the spatial overlay out.
      const spatialEnd = window.innerHeight * 6;
      setPastSpatial(window.scrollY > spatialEnd);

      const t =
        spatialEnd > 0
          ? Math.min(1, Math.max(0, window.scrollY / spatialEnd))
          : 0;
      const idx = Math.min(
        ZONE_ORDER.length - 1,
        Math.floor(t * ZONE_ORDER.length),
      );
      setZone(ZONE_ORDER[idx]);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (pastSpatial) return null;

  const { data: phaseTuple } = useReadContract({
    address: addresses.presale,
    abi: presaleAbi,
    functionName: "activePhase",
    query: { enabled: HAS_PRESALE, refetchInterval: 30_000 },
  });
  const { data: totalPurchased } = useReadContract({
    address: addresses.presale,
    abi: presaleAbi,
    functionName: "totalPurchased",
    query: { enabled: HAS_PRESALE, refetchInterval: 30_000 },
  });

  const priceE8 = phaseTuple?.[1]?.priceE8 ?? 3_370_000n;
  const sold = phaseTuple?.[1]?.soldTokens ?? 0n;
  const cap = phaseTuple?.[1]?.capTokens ?? 50_000_000n * 10n ** 18n;
  const endsAt =
    phaseTuple?.[1]?.endsAt ?? BigInt(Math.floor(Date.now() / 1000) + 30 * 86400);
  const pct = cap > 0n ? Number((sold * 10000n) / cap) / 100 : 0;
  const purchasedNum = Number((totalPurchased ?? sold) / 10n ** 18n);
  const priceUsd = Number(priceE8) / 1e8;
  const raisedUsd = purchasedNum * priceUsd;
  const totalSoldNum = Number(sold / 10n ** 18n);

  return (
    <div className="pointer-events-none fixed inset-0 z-20">
      <div className="mx-auto flex h-full max-w-7xl items-end px-4 pb-10 md:px-8 md:pb-16">
        <AnimatePresence mode="wait">
          {zone === "hero" && (
            <motion.div
              key="hero"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="pointer-events-auto max-w-2xl"
            >
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-success/40 bg-success/[0.06] px-3 py-1.5 text-xs font-medium text-success backdrop-blur-md">
                <span className="live-dot" />
                <span className="uppercase tracking-[0.2em]">
                  {HAS_PRESALE ? "Phase 1 Live · Base" : "Phase 0 · Pre-Deploy"}
                </span>
              </div>
              <h1 className="font-display text-[clamp(2.5rem,7.5vw,5rem)] leading-[0.92] tracking-tight text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.8)]">
                The creator economy
                <br />
                <span className="text-iridescent">runs on $FANZ.</span>
              </h1>
              <p className="mt-5 max-w-xl text-base text-gray-300 md:text-lg drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">
                Tokenized fan engagement, on-chain governance, and a money plane
                built for adults. Walk the corridor. Scroll to fly through. Or
                put on the headset.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a href="#presale" className="btn-primary">
                  <span className="live-dot text-white" />
                  Buy $FANZ now
                </a>
                <a href="#tokenomics" className="btn-secondary">
                  Tokenomics
                </a>
              </div>

              {/* Floating live stats panel */}
              <div className="mt-7 grid max-w-xl grid-cols-2 gap-2 rounded-2xl border border-white/[0.07] bg-black/55 p-3 backdrop-blur-2xl sm:grid-cols-4">
                <Stat label="Price" value={`$${priceUsd.toFixed(4)}`} />
                <Stat
                  label="Raised"
                  value={<AnimatedNumber value={raisedUsd} prefix="$" decimals={0} />}
                />
                <Stat
                  label="Sold"
                  value={<AnimatedNumber value={totalSoldNum} decimals={0} />}
                />
                <Stat label="APY" value="20%" highlight />
              </div>
              <div className="mt-3 max-w-xl">
                <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-300">
                  <span>Phase 1 progress</span>
                  <span className="font-mono text-white">
                    <AnimatedNumber value={pct} decimals={2} suffix="%" />
                  </span>
                </div>
                <ProgressBar pct={pct} />
                <div className="mt-2 flex items-center justify-between text-[10px] text-gray-300">
                  <span className="uppercase tracking-widest">Closes in</span>
                  <Countdown endsAt={endsAt} />
                </div>
              </div>
            </motion.div>
          )}

          {zone === "presale" && (
            <ZoneCopy
              key="presale"
              eyebrow="Presale Kiosk"
              title="Buy with USDC, USDT or ETH."
              body="The holographic terminal floats to your right. Type, approve, fire — all on Base. Auto-stake on claim for the 20% APY pool."
            />
          )}
          {zone === "tokenomics" && (
            <ZoneCopy
              key="tokenomics"
              eyebrow="Tokenomics"
              title="One billion FANZ. Eight buckets."
              body="Each crystal is a real allocation. Bigger = more supply. Hover (or raycast in VR) to see the bucket label and percentage."
            />
          )}
          {zone === "staking" && (
            <ZoneCopy
              key="staking"
              eyebrow="Staking"
              title="20% APY. Fixed. Real."
              body="The tornado is your principal. The hourglass is the 7-day cooldown. The widget is your wallet — stake, unstake, claim."
            />
          )}
          {zone === "roadmap" && (
            <ZoneCopy
              key="roadmap"
              eyebrow="Roadmap"
              title="Walk the timeline."
              body="Past milestones glow. The present beams light. The future shimmers in glass — coming, but not yet."
            />
          )}
          {zone === "footer" && (
            <ZoneCopy
              key="footer"
              eyebrow="Trust"
              title="On-chain. Audited. Open."
              body="Contracts on Base · Audited by Solid Proof + Coinsult · OpenZeppelin libraries · EIP-2612 permit · EIP-5805 votes."
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function ZoneCopy({
  eyebrow,
  title,
  body,
}: {
  eyebrow: string;
  title: string;
  body: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="pointer-events-auto max-w-xl"
    >
      <div className="mb-3 text-[10px] uppercase tracking-[0.22em] text-accent drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
        {eyebrow}
      </div>
      <h2 className="font-display text-[clamp(2rem,5vw,3.5rem)] leading-tight text-white drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]">
        {title}
      </h2>
      <p className="mt-3 text-sm text-gray-300 md:text-base drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
        {body}
      </p>
    </motion.div>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] uppercase tracking-[0.18em] text-gray-300">
        {label}
      </div>
      <div
        className={`mt-0.5 font-mono text-base font-semibold tabular-nums ${
          highlight ? "text-accent" : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
